import React, { useState, useEffect, useCallback } from 'react';
import {
    RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle, Eye, Edit, Save, Plus,
    Search, Building, CreditCard, Banknote, FileText, Brain, Send, X, Check,
    DollarSign, Calendar, Receipt, User, Package, MapPin, Phone, Mail, Wallet,
    ChevronDown, ChevronUp, Filter, Download, Upload, Settings, File,
    Users, Globe, Hash, Briefcase, Shield, AlertCircle,
    Copy, ExternalLink, Percent, Calculator, Clock4, Star, Zap, Sparkles
} from 'lucide-react';

const API_BASE_URL = 'https://api.zapbooksai.subraps.com/api';

// GST State Mapping
const GST_STATE_MAPPING = {
    '01': 'JK', '02': 'HP', '03': 'PB', '04': 'CH', '05': 'UT', '06': 'HR',
    '07': 'DL', '08': 'RJ', '09': 'UP', '10': 'BR', '11': 'SK', '12': 'AR',
    '13': 'NL', '14': 'MN', '15': 'MZ', '16': 'TR', '17': 'ML', '18': 'AS',
    '19': 'WB', '20': 'JH', '21': 'OR', '22': 'CG', '23': 'MP', '24': 'GJ',
    '25': 'DD', '26': 'DN', '27': 'MH', '28': 'AP', '29': 'KA', '30': 'GA',
    '31': 'LD', '32': 'KL', '33': 'TN', '34': 'PY', '35': 'AN', '36': 'TG',
    '37': 'AD', '38': 'LA'
};

const TransactionTypeIndicator = ({ type, hasGST, vendorType, reason }) => {
    const isBill = type === 'BILL';

    return (
        <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${isBill
            ? 'bg-blue-100 text-blue-800'
            : 'bg-green-100 text-green-800'
            }`}>
            {isBill ? (
                <>
                    <FileText className="w-3 h-3" />
                    Purchase Bill
                </>
            ) : (
                <>
                    <Wallet className="w-3 h-3" />
                    Expense
                </>
            )}
            <div className="text-xs opacity-75">
                {vendorType === 'EMPLOYEE_AS_VENDOR' ? '(Employee→Vendor)' : '(Actual Vendor)'}
            </div>
        </div>
    );
};

// Line Item Edit Modal Component
const LineItemEditModal = ({ isOpen, onClose, lineItem, onSave, expenseAccounts, loadingAccounts }) => {
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (lineItem && isOpen) {
            setFormData({
                name: lineItem.name || '',
                description: lineItem.description || '',
                rate: lineItem.rate || '',
                amount: lineItem.amount || '',
                quantity: lineItem.quantity || 1,
                sgst_rate: lineItem.sgst_rate || 0,
                cgst_rate: lineItem.cgst_rate || 0,
                igst_rate: lineItem.igst_rate || 0,
                expense_account_id: lineItem.expense_account_id || '',
                expense_account_name: lineItem.expense_account_name || '',
                itc_type: lineItem.itc_type || 'eligible',
                itc_percentage: lineItem.itc_percentage || ''
            });
            setErrors({});
        }
    }, [lineItem, isOpen]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        // Auto-calculate totals
        if (['rate', 'amount', 'quantity', 'sgst_rate', 'cgst_rate', 'igst_rate'].includes(field)) {
            const rate = parseFloat(field === 'rate' ? value : formData.rate) || 0;
            const amount = parseFloat(field === 'amount' ? value : formData.amount) || rate;
            const quantity = parseFloat(field === 'quantity' ? value : formData.quantity) || 1;
            const sgst = parseFloat(field === 'sgst_rate' ? value : formData.sgst_rate) || 0;
            const cgst = parseFloat(field === 'cgst_rate' ? value : formData.cgst_rate) || 0;
            const igst = parseFloat(field === 'igst_rate' ? value : formData.igst_rate) || 0;

            const baseAmount = amount * quantity;
            const taxRate = igst || (sgst + cgst);
            const taxAmount = baseAmount * (taxRate / 100);
            const totalWithTax = baseAmount + taxAmount;

            setFormData(prev => ({
                ...prev,
                total_tax: taxAmount.toFixed(2),
                total_with_tax: totalWithTax.toFixed(2),
                sgst_amount: igst ? 0 : (baseAmount * sgst / 100).toFixed(2),
                cgst_amount: igst ? 0 : (baseAmount * cgst / 100).toFixed(2),
                igst_amount: igst ? taxAmount.toFixed(2) : 0
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name?.trim()) newErrors.name = 'Item name is required';
        if (!formData.amount || parseFloat(formData.amount) <= 0) newErrors.amount = 'Valid amount is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validateForm()) return;
        onSave(formData);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Edit Line Item</h3>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div className="p-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Item Name *
                            </label>
                            <input
                                type="text"
                                value={formData.name || ''}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                className={`w-full border rounded-md px-3 py-2 ${errors.name ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500`}
                                placeholder="Enter item name"
                            />
                            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Description
                            </label>
                            <input
                                type="text"
                                value={formData.description || ''}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter description"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Amount *
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.amount || ''}
                                onChange={(e) => handleInputChange('amount', e.target.value)}
                                className={`w-full border rounded-md px-3 py-2 ${errors.amount ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500`}
                                placeholder="Enter amount"
                            />
                            {errors.amount && <p className="text-red-500 text-sm mt-1">{errors.amount}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Quantity
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.quantity || 1}
                                onChange={(e) => handleInputChange('quantity', e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter quantity"
                            />
                        </div>
                    </div>

                    {/* GST Rates */}
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">SGST %</label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.sgst_rate || ''}
                                onChange={(e) => handleInputChange('sgst_rate', e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                                placeholder="0.00"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">CGST %</label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.cgst_rate || ''}
                                onChange={(e) => handleInputChange('cgst_rate', e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                                placeholder="0.00"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">IGST %</label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.igst_rate || ''}
                                onChange={(e) => handleInputChange('igst_rate', e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                                placeholder="0.00"
                            />
                        </div>
                    </div>

                    {/* Expense Account */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Expense Account
                        </label>
                        <select
                            value={formData.expense_account_id || ''}
                            onChange={(e) => {
                                const selectedAccount = expenseAccounts.find(acc => acc.account_id === e.target.value);
                                handleInputChange('expense_account_id', e.target.value);
                                if (selectedAccount) {
                                    handleInputChange('expense_account_name', selectedAccount.account_name);
                                }
                            }}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                            disabled={loadingAccounts}
                        >
                            <option value="">Select expense account</option>
                            {expenseAccounts.map(account => (
                                <option key={account.account_id} value={account.account_id}>
                                    {account.account_name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* ITC for GST items */}
                    {(parseFloat(formData.sgst_rate || 0) > 0 || parseFloat(formData.cgst_rate || 0) > 0 || parseFloat(formData.igst_rate || 0) > 0) && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Input Tax Credit (ITC)
                            </label>
                            <select
                                value={formData.itc_type || 'eligible'}
                                onChange={(e) => handleInputChange('itc_type', e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="eligible">Eligible for ITC</option>
                                <option value="ineligible">Not Eligible for ITC</option>
                                <option value="partial">Partial ITC</option>
                            </select>
                            {formData.itc_type === 'partial' && (
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    placeholder="ITC %"
                                    value={formData.itc_percentage || ''}
                                    onChange={(e) => handleInputChange('itc_percentage', e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 mt-2 focus:ring-2 focus:ring-blue-500"
                                />
                            )}
                        </div>
                    )}

                    {/* Calculated Values Display */}
                    {formData.total_with_tax && (
                        <div className="bg-gray-50 p-3 rounded-lg">
                            <h4 className="text-sm font-medium text-gray-900 mb-2">Calculated Values</h4>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                <div>Base Amount: ₹{(parseFloat(formData.amount || 0) * parseFloat(formData.quantity || 1)).toFixed(2)}</div>
                                <div>Tax Amount: ₹{formData.total_tax || '0.00'}</div>
                                <div className="font-medium">Total with Tax: ₹{formData.total_with_tax || '0.00'}</div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                    >
                        <Save className="w-4 h-4" />
                        Save Changes
                    </button>
                </div>
            </div>
        </div>
    );
};

// Enhanced Edit Modal Component with AI Integration
const EditReimbursementModal = ({ reimbursement, isOpen, onClose, onSave, getAuthHeaders }) => {
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [expenseAccounts, setExpenseAccounts] = useState([]);
    const [paymentAccounts, setPaymentAccounts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [aiSuggesting, setAiSuggesting] = useState(false);

    // Line items state with ITC support
    const [lineItems, setLineItems] = useState([]);
    const [showApplyToAll, setShowApplyToAll] = useState(false);
    const [applyToAllAccount, setApplyToAllAccount] = useState('');

    // Add these state variables at the top of EditReimbursementModal
    const [editingLineItem, setEditingLineItem] = useState(null);
    const [editingLineItemIndex, setEditingLineItemIndex] = useState(-1);
    const [lineItemEditModalOpen, setLineItemEditModalOpen] = useState(false);

    // ADD APPROVAL STATUS CHECK
    const canEditReimbursement = useCallback(() => {
        const status = reimbursement?.approver_status ||
            reimbursement?.approvalInfo?.status ||
            reimbursement?.approval_status;

        console.log('EditModal - Checking approval status:', {
            reimbursement_id: reimbursement?.id,
            approver_status: reimbursement?.approver_status,
            status: status,
            canEdit: status === 'approved' || !status
        });

        // Only allow editing if approved or no status (defaults to approved)
        return status === 'approved' || !status;
    }, [reimbursement]);

    // CHECK APPROVAL STATUS BEFORE RENDERING FORM
    if (isOpen && !canEditReimbursement()) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-lg max-w-md w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold text-gray-900">Cannot Edit Reimbursement</h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    <div className="flex items-center gap-3 mb-4">
                        <AlertTriangle className="w-8 h-8 text-yellow-500" />
                        <div>
                            <h3 className="font-medium text-gray-900">Editing Restricted</h3>
                            <p className="text-sm text-gray-600">
                                This reimbursement cannot be edited because it is:
                            </p>
                        </div>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center gap-2">
                            {reimbursement?.approver_status === 'pending_approval' && (
                                <>
                                    <Clock className="w-4 h-4 text-yellow-600" />
                                    <span className="text-sm font-medium text-yellow-800">Pending Approval</span>
                                </>
                            )}
                            {reimbursement?.approver_status === 'rejected' && (
                                <>
                                    <XCircle className="w-4 h-4 text-red-600" />
                                    <span className="text-sm font-medium text-red-800">Rejected</span>
                                </>
                            )}
                            {!reimbursement?.approver_status && (
                                <>
                                    <AlertCircle className="w-4 h-4 text-gray-600" />
                                    <span className="text-sm font-medium text-gray-800">Status Unknown</span>
                                </>
                            )}
                        </div>
                        {reimbursement?.rejection_reason && (
                            <p className="text-xs text-gray-700 mt-2">
                                <strong>Reason:</strong> {reimbursement.rejection_reason}
                            </p>
                        )}
                    </div>

                    <div className="text-sm text-gray-600 mb-4">
                        Only approved reimbursements can be edited. Please contact an approver to change the status if needed.
                    </div>

                    <button
                        onClick={onClose}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        Close
                    </button>
                </div>
            </div>
        );
    }

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            console.error('Date formatting error:', error);
            return '';
        }
    };

    const handleDateChange = (field, value) => {
        if (field === 'date' && value) {
            const localDate = new Date(value + 'T00:00:00');
            handleInputChange(field, localDate.toISOString());
        } else {
            handleInputChange(field, value);
        }
    };

    // AI Suggestion Functions
    const fetchAISuggestions = async () => {
        if (!reimbursement?.organization?.organization_id) return;

        setAiSuggesting(true);
        try {
            console.log('🤖 Starting AI suggestions...');

            const hasGST = parseFloat(formData.sgst_rate || 0) > 0 ||
                parseFloat(formData.cgst_rate || 0) > 0 ||
                parseFloat(formData.igst_rate || 0) > 0;

            // Suggest payment account for expenses (if has GST)
            if (hasGST) {
                try {
                    const paymentResponse = await fetch(`${API_BASE_URL}/ai/suggest-payment-account`, {
                        method: 'POST',
                        headers: getAuthHeaders(),
                        body: JSON.stringify({
                            organization_id: reimbursement.organization.organization_id
                        })
                    });

                    if (paymentResponse.ok) {
                        const paymentData = await paymentResponse.json();
                        if (paymentData.success && paymentData.data) {
                            setFormData(prev => ({
                                ...prev,
                                payment_account_id: paymentData.data.account_id,
                                payment_account_name: paymentData.data.account_name
                            }));
                            console.log('🤖 AI suggested payment account:', paymentData.data.account_name);
                        }
                    }
                } catch (error) {
                    console.error('Payment account suggestion error:', error);
                }
            }

            // FIX: Corrected single item detection - check if NO line items AND items field exists
            if (lineItems.length === 0 && formData.items && expenseAccounts.length > 0) {
                console.log('🎯 This is a TRUE single item (no line_items_data):', formData.items);

                try {
                    console.log('🎯 Suggesting for single item (CORRECTED):', formData.items);

                    const expenseResponse = await fetch(`${API_BASE_URL}/ai/suggest-expense-account`, {
                        method: 'POST',
                        headers: getAuthHeaders(),
                        body: JSON.stringify({
                            item_description: formData.items,
                            organization_id: reimbursement.organization.organization_id
                        })
                    });

                    if (expenseResponse.ok) {
                        const expenseData = await expenseResponse.json();
                        console.log('🤖 Single item AI response (CORRECTED):', expenseData);

                        if (expenseData.success && expenseData.data) {
                            const matchingAccount = expenseAccounts.find(acc => acc.account_id === expenseData.data.account_id);
                            if (matchingAccount) {
                                console.log('✅ Found matching account, updating state (CORRECTED)...');

                                setFormData(prev => {
                                    const newData = {
                                        ...prev,
                                        zoho_expense_account_id: expenseData.data.account_id,
                                        zoho_expense_account_name: expenseData.data.account_name
                                    };
                                    console.log('📝 Updated formData (CORRECTED):', newData.zoho_expense_account_id, newData.zoho_expense_account_name);
                                    return newData;
                                });

                                console.log('🤖 AI suggested single item expense account (CORRECTED):', expenseData.data.account_name, 'ID:', expenseData.data.account_id);
                            } else {
                                console.log('❌ Suggested account not found in dropdown list:', expenseData.data.account_id);
                                console.log('Available accounts:', expenseAccounts.map(acc => `${acc.account_id}: ${acc.account_name}`));
                            }
                        }
                    }
                } catch (error) {
                    console.error('Single item expense account suggestion error (CORRECTED):', error);
                }
            } else if (lineItems.length > 0) {
                console.log('🔗 This has line items, not a single item. Line items count:', lineItems.length);
            } else {
                console.log('⚠️ No items description or no expense accounts loaded');
            }

            // Suggest expense accounts for line items (existing logic)
            if (lineItems.length > 0) {
                const updatedItems = [...lineItems];
                let hasUpdates = false;

                for (let i = 0; i < updatedItems.length; i++) {
                    const item = updatedItems[i];
                    if (!item.expense_account_id && (item.description || item.name)) {
                        try {
                            const response = await fetch(`${API_BASE_URL}/ai/suggest-expense-account`, {
                                method: 'POST',
                                headers: getAuthHeaders(),
                                body: JSON.stringify({
                                    item_description: item.description || item.name,
                                    organization_id: reimbursement.organization.organization_id
                                })
                            });

                            if (response.ok) {
                                const data = await response.json();
                                if (data.success && data.data) {
                                    updatedItems[i] = {
                                        ...item,
                                        expense_account_id: data.data.account_id,
                                        expense_account_name: data.data.account_name
                                    };
                                    hasUpdates = true;
                                    console.log(`🤖 AI suggested for line item "${item.name}": ${data.data.account_name} (${data.data.source})`);
                                }
                            }
                        } catch (error) {
                            console.error(`Line item suggestion error for ${item.name}:`, error);
                        }
                    }
                }

                if (hasUpdates) {
                    setLineItems(updatedItems);
                    setFormData(prev => ({
                        ...prev,
                        line_items_data: JSON.stringify(updatedItems)
                    }));
                }
            }

        } catch (error) {
            console.error('Error fetching AI suggestions:', error);
        } finally {
            setAiSuggesting(false);
        }
    };

    useEffect(() => {
        if (reimbursement && isOpen) {
            setFormData({
                invoice_no: reimbursement.invoice_no || '',
                employee_name: reimbursement.employee_name || '',
                vendor_name: reimbursement.vendor_name || '',
                date: reimbursement.date || '',
                items: reimbursement.items || '',
                amount: reimbursement.amount || '',
                sgst_rate: reimbursement.sgst_rate || 0,
                cgst_rate: reimbursement.cgst_rate || 0,
                igst_rate: reimbursement.igst_rate || 0,
                total: reimbursement.total || '',
                currency: reimbursement.currency || 'INR',
                gstin: reimbursement.gstin || '',
                notes: reimbursement.notes || '',
                // FIX: Proper boolean handling for is_inclusive_tax
                is_inclusive_tax: reimbursement.is_inclusive_tax === true || reimbursement.is_inclusive_tax === 1,
                payment_account_id: reimbursement.payment_account_id || '',
                payment_account_name: reimbursement.payment_account_name || '',
                zoho_expense_account_id: reimbursement.zoho_expense_account_id || '',
                zoho_expense_account_name: reimbursement.zoho_expense_account_name || '',
                // ITC FIELDS
                itc_type: reimbursement.itc_type || 'eligible',
                itc_percentage: reimbursement.itc_percentage || ''
            });

            // Parse line items - this determines if it's single item or multi-line
            if (reimbursement.line_items_data) {
                try {
                    const items = typeof reimbursement.line_items_data === 'string'
                        ? JSON.parse(reimbursement.line_items_data)
                        : reimbursement.line_items_data;

                    const itemsWithITC = Array.isArray(items) ? items.map(item => ({
                        ...item,
                        itc_type: item.itc_type || 'eligible',
                        itc_percentage: item.itc_percentage || ''
                    })) : [];

                    setLineItems(itemsWithITC);
                    setShowApplyToAll(itemsWithITC.length > 1);
                    console.log('📋 Loaded line items:', itemsWithITC.length);
                } catch (error) {
                    console.log('❌ Failed to parse line items, treating as single item');
                    setLineItems([]);
                    setShowApplyToAll(false);
                }
            } else {
                console.log('📝 No line items data, this is a single item');
                setLineItems([]);
                setShowApplyToAll(false);
            }

            setErrors({});
            fetchAccounts();
        }
    }, [reimbursement, isOpen]);

    const fetchAccounts = async () => {
        if (!reimbursement?.organization?.organization_id) return;

        setLoadingAccounts(true);
        try {
            const [expenseResponse, paymentResponse] = await Promise.all([
                fetch(`${API_BASE_URL}/accounts/expense?company_id=${reimbursement.organization.organization_id}`, {
                    headers: getAuthHeaders()
                }),
                fetch(`${API_BASE_URL}/accounts/payment?company_id=${reimbursement.organization.organization_id}`, {
                    headers: getAuthHeaders()
                })
            ]);

            if (expenseResponse.ok) {
                const expenseData = await expenseResponse.json();
                if (expenseData.success) {
                    setExpenseAccounts(expenseData.data);
                }
            }

            if (paymentResponse.ok) {
                const paymentData = await paymentResponse.json();
                if (paymentData.success) {
                    setPaymentAccounts(paymentData.data);
                }
            }

            // Auto-trigger AI suggestions after accounts are loaded
            setTimeout(() => {
                fetchAISuggestions();
            }, 500);

        } catch (error) {
            console.error('Error fetching accounts:', error);
        } finally {
            setLoadingAccounts(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }

        // FIX: Proper tax calculation with correct is_inclusive_tax handling
        if (['amount', 'sgst_rate', 'cgst_rate', 'igst_rate', 'is_inclusive_tax'].includes(field)) {
            const amount = parseFloat(field === 'amount' ? value : formData.amount) || 0;
            const sgst = parseFloat(field === 'sgst_rate' ? value : formData.sgst_rate) || 0;
            const cgst = parseFloat(field === 'cgst_rate' ? value : formData.cgst_rate) || 0;
            const igst = parseFloat(field === 'igst_rate' ? value : formData.igst_rate) || 0;

            // FIX: Get the current checkbox state properly
            const isInclusive = field === 'is_inclusive_tax' ? value : (formData.is_inclusive_tax !== false);

            const taxRate = igst || (sgst + cgst);
            const total = isInclusive
                ? amount // If inclusive, total = amount
                : amount + (amount * taxRate / 100); // If exclusive, add tax

            setFormData(prev => ({
                ...prev,
                total: total.toFixed(2),
                // FIX: Ensure is_inclusive_tax is properly set
                ...(field === 'is_inclusive_tax' && { is_inclusive_tax: value })
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.invoice_no?.trim()) {
            newErrors.invoice_no = 'Invoice number is required';
        }
        if (!formData.employee_name?.trim()) {
            newErrors.employee_name = 'Employee name is required';
        }
        if (!formData.items?.trim()) {
            newErrors.items = 'Items description is required';
        }
        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            newErrors.amount = 'Valid amount is required';
        }

        const hasGST = parseFloat(formData.sgst_rate || 0) > 0 ||
            parseFloat(formData.cgst_rate || 0) > 0 ||
            parseFloat(formData.igst_rate || 0) > 0;

        if (hasGST && !formData.vendor_name?.trim()) {
            newErrors.vendor_name = 'Vendor name is required for GST transactions';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setLoading(true);
        try {
            const saveData = {
                ...formData,
                line_items_data: lineItems.length > 0 ? JSON.stringify(lineItems) : formData.line_items_data
            };

            console.log('Sending save data with ITC:', saveData);

            const response = await fetch(`${API_BASE_URL}/reimbursements/${reimbursement.id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(saveData)
            });

            const result = await response.json();

            if (result.success) {
                onSave(result.data);
                onClose();
            } else {
                throw new Error(result.message || 'Failed to update reimbursement');
            }
        } catch (error) {
            console.error('Error updating reimbursement:', error);
            alert('Failed to update reimbursement: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleLineItemChange = (index, field, value) => {
        const updatedItems = [...lineItems];
        updatedItems[index] = { ...updatedItems[index], [field]: value };

        if (field === 'expense_account_id') {
            const selectedAccount = expenseAccounts.find(acc => acc.account_id === value);
            updatedItems[index].expense_account_name = selectedAccount?.account_name || '';
        }

        setLineItems(updatedItems);
        setFormData(prev => ({
            ...prev,
            line_items_data: JSON.stringify(updatedItems)
        }));
    };

    const applyAccountToAll = () => {
        if (!applyToAllAccount) return;

        const selectedAccount = expenseAccounts.find(acc => acc.account_id === applyToAllAccount);
        const updatedItems = lineItems.map(item => ({
            ...item,
            expense_account_id: applyToAllAccount,
            expense_account_name: selectedAccount?.account_name || ''
        }));

        setLineItems(updatedItems);
        setFormData(prev => ({
            ...prev,
            line_items_data: JSON.stringify(updatedItems)
        }));
        setApplyToAllAccount('');
    };

    // Add these functions before the return statement
    const handleEditLineItem = (item, index) => {
        setEditingLineItem(item);
        setEditingLineItemIndex(index);
        setLineItemEditModalOpen(true);
    };

    const handleSaveLineItem = async (updatedLineItem) => {
        try {
            const response = await fetch(`${API_BASE_URL}/reimbursements/${reimbursement.id}/line-item`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    lineItemIndex: editingLineItemIndex,
                    lineItemData: updatedLineItem
                })
            });

            const result = await response.json();
            if (result.success) {
                // Update local state
                const updatedItems = [...lineItems];
                updatedItems[editingLineItemIndex] = updatedLineItem;
                setLineItems(updatedItems);
                setFormData(prev => ({
                    ...prev,
                    line_items_data: JSON.stringify(updatedItems)
                }));

                // Trigger parent refresh
                onSave(result.data);
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            console.error('Error updating line item:', error);
            alert('Failed to update line item: ' + error.message);
        }
    };

    const handleDeleteLineItem = async (index) => {
        if (lineItems.length <= 1) {
            alert('Cannot delete the last remaining line item');
            return;
        }

        if (!confirm('Are you sure you want to delete this line item?')) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/reimbursements/${reimbursement.id}/line-item`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    lineItemIndex: index
                })
            });

            const result = await response.json();
            if (result.success) {
                // Update local state
                const updatedItems = lineItems.filter((_, i) => i !== index);
                setLineItems(updatedItems);
                setFormData(prev => ({
                    ...prev,
                    line_items_data: JSON.stringify(updatedItems)
                }));

                // Trigger parent refresh
                onSave(result.data);
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            console.error('Error deleting line item:', error);
            alert('Failed to delete line item: ' + error.message);
        }
    };

    const hasGST = parseFloat(formData.sgst_rate || 0) > 0 ||
        parseFloat(formData.cgst_rate || 0) > 0 ||
        parseFloat(formData.igst_rate || 0) > 0;

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900">Edit Reimbursement</h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    {/* Transaction Type Preview */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-900 mb-2">Transaction Type Preview</h3>
                        <TransactionTypeIndicator
                            type={hasGST ? 'EXPENSE' : 'BILL'}
                            hasGST={hasGST}
                            vendorType={hasGST ? 'ACTUAL_VENDOR' : 'EMPLOYEE_AS_VENDOR'}
                            reason={hasGST ? 'Has GST - Create Expense with actual vendor' : 'No GST - Create Bill with employee as vendor'}
                        />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Basic Information */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Invoice Number *
                                </label>
                                <input
                                    type="text"
                                    value={formData.invoice_no || ''}
                                    onChange={(e) => handleInputChange('invoice_no', e.target.value)}
                                    className={`w-full border rounded-md px-3 py-2 ${errors.invoice_no ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                    placeholder="Enter invoice number"
                                />
                                {errors.invoice_no && (
                                    <p className="text-red-500 text-sm mt-1">{errors.invoice_no}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Employee Name *
                                </label>
                                <input
                                    type="text"
                                    value={formData.employee_name || ''}
                                    onChange={(e) => handleInputChange('employee_name', e.target.value)}
                                    className={`w-full border rounded-md px-3 py-2 ${errors.employee_name ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                    placeholder="Enter employee name"
                                />
                                {errors.employee_name && (
                                    <p className="text-red-500 text-sm mt-1">{errors.employee_name}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Vendor Name {hasGST ? '*' : '(Optional)'}
                                </label>
                                <input
                                    type="text"
                                    value={formData.vendor_name || ''}
                                    onChange={(e) => handleInputChange('vendor_name', e.target.value)}
                                    className={`w-full border rounded-md px-3 py-2 ${errors.vendor_name ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                    placeholder={hasGST ? "Enter vendor name (required for GST)" : "Enter vendor name (optional for no GST)"}
                                />
                                {errors.vendor_name && (
                                    <p className="text-red-500 text-sm mt-1">{errors.vendor_name}</p>
                                )}
                                {hasGST && (
                                    <p className="text-xs text-amber-600 mt-1">Required for GST transactions</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Date
                                </label>
                                <input
                                    type="date"
                                    value={formatDateForInput(formData.date)}
                                    onChange={(e) => handleDateChange('date', e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    GSTIN {hasGST ? '(Recommended)' : '(Not needed for No GST)'}
                                </label>
                                <input
                                    type="text"
                                    value={formData.gstin || ''}
                                    onChange={(e) => handleInputChange('gstin', e.target.value.toUpperCase())}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter vendor GSTIN"
                                    maxLength={15}
                                />
                            </div>
                        </div>

                        {/* Financial Information */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium text-gray-900">Financial Details</h3>

                            {/* Payment Account Selection (for Expenses only) */}
                            {hasGST && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Payment Account * (Employee Reimbursement Account)
                                    </label>
                                    <select
                                        value={formData.payment_account_id || ''}
                                        onChange={(e) => {
                                            const selectedAccount = paymentAccounts.find(acc => acc.account_id === e.target.value);
                                            handleInputChange('payment_account_id', e.target.value);
                                            if (selectedAccount) {
                                                handleInputChange('payment_account_name', selectedAccount.account_name);
                                            }
                                        }}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                                        disabled={loadingAccounts}
                                    >
                                        <option value="">Select Payment Account</option>
                                        {paymentAccounts.map(account => (
                                            <option key={account.account_id} value={account.account_id}>
                                                {account.account_name} ({account.account_type})
                                            </option>
                                        ))}
                                    </select>
                                    {formData.payment_account_name && (
                                        <div className="text-xs text-green-600 mt-1">
                                            Selected: {formData.payment_account_name}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Items Description *
                                </label>
                                <textarea
                                    value={formData.items || ''}
                                    onChange={(e) => handleInputChange('items', e.target.value)}
                                    rows={3}
                                    className={`w-full border rounded-md px-3 py-2 ${errors.items ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                    placeholder="Enter items description"
                                />
                                {errors.items && (
                                    <p className="text-red-500 text-sm mt-1">{errors.items}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Amount * {hasGST ? '(Base amount for tax calculation)' : '(Total inclusive amount)'}
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.amount || ''}
                                    onChange={(e) => handleInputChange('amount', e.target.value)}
                                    className={`w-full border rounded-md px-3 py-2 ${errors.amount ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                    placeholder="Enter amount"
                                />
                                {errors.amount && (
                                    <p className="text-red-500 text-sm mt-1">{errors.amount}</p>
                                )}
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        SGST %
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.sgst_rate || ''}
                                        onChange={(e) => handleInputChange('sgst_rate', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="0.00"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        CGST %
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.cgst_rate || ''}
                                        onChange={(e) => handleInputChange('cgst_rate', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="0.00"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        IGST %
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.igst_rate || ''}
                                        onChange={(e) => handleInputChange('igst_rate', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Total Amount
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.total || ''}
                                    onChange={(e) => handleInputChange('total', e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter total amount"
                                />
                            </div> */}

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="is_inclusive_tax"
                                    checked={formData.is_inclusive_tax !== false} // FIX: Proper boolean handling
                                    onChange={(e) => {
                                        console.log('Tax inclusive checkbox changed:', e.target.checked);
                                        handleInputChange('is_inclusive_tax', e.target.checked); // FIX: Use handleInputChange for proper state update
                                    }}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <label htmlFor="is_inclusive_tax" className="ml-2 text-sm text-gray-700">
                                    Tax Inclusive {!hasGST && '(recommended for no GST)'}
                                </label>
                                {/* DEBUG: Show current state */}
                                <div className="text-xs text-gray-500 ml-2">
                                    Current: {formData.is_inclusive_tax !== false ? 'Inclusive' : 'Exclusive'}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Currency
                                </label>
                                <select
                                    value={formData.currency || 'INR'}
                                    onChange={(e) => handleInputChange('currency', e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="INR">INR</option>
                                    <option value="USD">USD</option>
                                    <option value="EUR">EUR</option>
                                    <option value="GBP">GBP</option>
                                </select>
                            </div>

                            {/* <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Notes
                                </label>
                                <textarea
                                    value={formData.notes || ''}
                                    onChange={(e) => handleInputChange('notes', e.target.value)}
                                    rows={3}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter any notes"
                                />
                            </div> */}
                        </div>

                        {/* Line Items and Accounts with AI - FIXED LOGIC */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-medium text-gray-900">Expense Accounts</h3>
                                {/* 
                                FIXED LOGIC FOR AI SUGGEST ALL BUTTON:
                                - For Bills (No GST): Only show for line items (multiple items)
                                - For Expenses (Has GST): Show for both single items and line items
                                */}
                                {((hasGST) || (lineItems.length > 0)) && (
                                    <button
                                        type="button"
                                        onClick={fetchAISuggestions}
                                        disabled={aiSuggesting}
                                        className="flex items-center gap-2 px-3 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 text-sm"
                                    >
                                        {aiSuggesting ? (
                                            <>
                                                <RefreshCw className="w-4 h-4 animate-spin" />
                                                AI Suggesting...
                                            </>
                                        ) : (
                                            <>
                                                <Sparkles className="w-4 h-4" />
                                                AI Suggest All
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>

                            {/* Apply to All Section - Only for line items */}
                            {showApplyToAll && expenseAccounts.length > 0 && lineItems.length > 1 && (
                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <h4 className="text-sm font-medium text-blue-900 mb-2">Apply Account to All Items</h4>
                                    <div className="flex gap-2">
                                        <select
                                            value={applyToAllAccount}
                                            onChange={(e) => setApplyToAllAccount(e.target.value)}
                                            className="flex-1 border border-blue-300 rounded-md px-3 py-2"
                                            disabled={loadingAccounts}
                                        >
                                            <option value="">Select account to apply to all</option>
                                            {expenseAccounts.map(account => (
                                                <option key={account.account_id} value={account.account_id}>
                                                    {account.account_name}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            onClick={applyAccountToAll}
                                            disabled={!applyToAllAccount}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                        >
                                            Apply to All
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Line Items with ITC Support */}
                            {lineItems.length > 0 ? (
                                <div className="space-y-3">
                                    <h4 className="text-sm font-medium text-gray-900">Line Items</h4>
                                    {lineItems.map((item, index) => (
                                        <div key={index} className="border rounded-lg p-3 bg-gray-50">
                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex-1">
                                                    <div className="text-sm font-medium text-gray-900 mb-1">
                                                        {item.name || item.description}
                                                    </div>
                                                    <div className="text-xs text-gray-600 mb-2">
                                                        Rate: ₹{item.amount} × {item.quantity || 1} = ₹{item.total_with_tax || item.amount}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1 ml-2">
                                                    <button
                                                        onClick={() => handleEditLineItem(item, index)}
                                                        className="p-1 text-blue-600 hover:text-blue-800"
                                                        title="Edit line item"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteLineItem(index)}
                                                        className="p-1 text-red-600 hover:text-red-800"
                                                        title="Delete line item"
                                                        disabled={lineItems.length <= 1}
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Expense Account Selection */}
                                            <select
                                                value={item.expense_account_id || ''}
                                                onChange={(e) => handleLineItemChange(index, 'expense_account_id', e.target.value)}
                                                className="w-full text-sm border border-gray-300 rounded-md px-2 py-1 mb-2"
                                                disabled={loadingAccounts}
                                            >
                                                <option value="">Select expense account</option>
                                                {expenseAccounts.map(account => (
                                                    <option key={account.account_id} value={account.account_id}>
                                                        {account.account_name}
                                                    </option>
                                                ))}
                                            </select>

                                            {/* ITC Selection for GST items */}
                                            {(parseFloat(item.sgst_rate || 0) > 0 || parseFloat(item.cgst_rate || 0) > 0 || parseFloat(item.igst_rate || 0) > 0) && (
                                                <div className="mt-2 border-t pt-2">
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                        Input Tax Credit (ITC)
                                                    </label>
                                                    <select
                                                        value={item.itc_type || 'eligible'}
                                                        onChange={(e) => handleLineItemChange(index, 'itc_type', e.target.value)}
                                                        className="w-full text-xs border border-gray-300 rounded-md px-2 py-1"
                                                    >
                                                        <option value="eligible">Eligible for ITC</option>
                                                        <option value="ineligible">Not Eligible for ITC</option>
                                                        <option value="partial">Partial ITC</option>
                                                    </select>
                                                    {item.itc_type === 'partial' && (
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            max="100"
                                                            placeholder="ITC %"
                                                            value={item.itc_percentage || ''}
                                                            onChange={(e) => handleLineItemChange(index, 'itc_percentage', e.target.value)}
                                                            className="w-full text-xs border border-gray-300 rounded-md px-2 py-1 mt-1"
                                                        />
                                                    )}
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        {item.itc_type === 'eligible' && '✅ Full input tax credit can be claimed'}
                                                        {item.itc_type === 'ineligible' && '❌ No input tax credit available'}
                                                        {item.itc_type === 'partial' && `📊 ${item.itc_percentage || 0}% input tax credit available`}
                                                    </div>
                                                </div>
                                            )}

                                            {item.expense_account_id && (
                                                <div className="text-xs text-green-600 mt-1">
                                                    ✅ Account: {expenseAccounts.find(acc => acc.account_id === item.expense_account_id)?.account_name}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                // Single item - FIXED VERSION
                                <div className="border rounded-lg p-4 bg-gray-50">
                                    <div className="flex items-center justify-between mb-2">
                                        <h4 className="text-sm font-medium text-gray-900">Single Item</h4>
                                        {/* Show individual AI suggest button only for single items when no "AI Suggest All" is shown */}
                                        {!hasGST && (
                                            <button
                                                type="button"
                                                onClick={fetchAISuggestions}
                                                disabled={aiSuggesting}
                                                className="flex items-center gap-1 px-2 py-1 bg-purple-500 text-white text-xs rounded hover:bg-purple-600 disabled:opacity-50"
                                            >
                                                {aiSuggesting ? (
                                                    <>
                                                        <RefreshCw className="w-3 h-3 animate-spin" />
                                                        AI...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Sparkles className="w-3 h-3" />
                                                        AI Suggest
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>

                                    <select
                                        value={formData.zoho_expense_account_id || ''}
                                        onChange={(e) => {
                                            const selectedAccount = expenseAccounts.find(acc => acc.account_id === e.target.value);
                                            console.log('Manual account selection:', e.target.value, selectedAccount?.account_name);
                                            handleInputChange('zoho_expense_account_id', e.target.value);
                                            if (selectedAccount) {
                                                handleInputChange('zoho_expense_account_name', selectedAccount.account_name);
                                            }
                                        }}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3"
                                        disabled={loadingAccounts}
                                    >
                                        <option value="">Select expense account</option>
                                        {expenseAccounts.map(account => (
                                            <option key={account.account_id} value={account.account_id}>
                                                {account.account_name}
                                            </option>
                                        ))}
                                    </select>

                                    {/* Show selected account confirmation */}
                                    {formData.zoho_expense_account_id && formData.zoho_expense_account_name && (
                                        <div className="text-xs text-green-600 mt-1 bg-green-50 p-2 rounded">
                                            ✅ Selected: {formData.zoho_expense_account_name}
                                            <div className="text-xs text-gray-500">ID: {formData.zoho_expense_account_id}</div>
                                        </div>
                                    )}

                                    {/* Show if account selected but name not found */}
                                    {formData.zoho_expense_account_id && !formData.zoho_expense_account_name && (
                                        <div className="text-xs text-amber-600 mt-1">
                                            ⚠️ Account selected but name not found - please reselect
                                        </div>
                                    )}

                                    {/* ITC for single item if it has GST */}
                                    {hasGST && (
                                        <div className="mt-3">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Input Tax Credit (ITC)
                                            </label>
                                            <select
                                                value={formData.itc_eligibility || 'eligible'}
                                                onChange={(e) => handleInputChange('itc_eligibility', e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 mb-2"
                                            >
                                                <option value="eligible">Eligible for ITC</option>
                                                <option value="ineligible_section17">Ineligible - Section 17(5)</option>
                                                <option value="ineligible_others">Ineligible - Others</option>
                                            </select>
                                            <div className="text-sm text-gray-600 mt-2">
                                                {formData.itc_eligibility === 'eligible' && '✅ Full input tax credit can be claimed on this expense'}
                                                {formData.itc_eligibility === 'ineligible_section17' && '❌ Not eligible as per Section 17(5) of CGST Act'}
                                                {formData.itc_eligibility === 'ineligible_others' && '❌ Not eligible for input tax credit - Other reasons'}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {loadingAccounts && (
                                <div className="text-sm text-gray-500 flex items-center gap-2">
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                    Loading accounts...
                                </div>
                            )}
                        </div>
                    </div>
                    <LineItemEditModal
                        isOpen={lineItemEditModalOpen}
                        onClose={() => {
                            setLineItemEditModalOpen(false);
                            setEditingLineItem(null);
                            setEditingLineItemIndex(-1);
                        }}
                        lineItem={editingLineItem}
                        onSave={handleSaveLineItem}
                        expenseAccounts={expenseAccounts}
                        loadingAccounts={loadingAccounts}
                    />
                </div>

                <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                    >
                        {loading ? (
                            <>
                                <RefreshCw className="w-4 h-4 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4" />
                                Save Changes
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

const InvoiceNumberEditor = ({ reimbursement, onUpdate, getAuthHeaders }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(reimbursement.invoice_no || '');
    const [saving, setSaving] = useState(false);

    const canEdit = reimbursement.zoho_sync_status !== 'synced';

    const handleSave = async () => {
        if (!value.trim()) {
            alert('Invoice number cannot be empty');
            return;
        }

        setSaving(true);
        try {
            const response = await fetch(`${API_BASE_URL}/reimbursements/${reimbursement.id}/invoice-number`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ invoice_no: value.trim() })
            });

            const result = await response.json();

            if (result.success) {
                onUpdate(result.data);
                setIsEditing(false);
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            console.error('Error updating invoice number:', error);
            alert('Failed to update invoice number: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setValue(reimbursement.invoice_no || '');
        setIsEditing(false);
    };

    if (!canEdit && reimbursement.invoice_no) {
        return <span className="font-medium">#{reimbursement.invoice_no}</span>;
    }

    if (!isEditing) {
        return (
            <div className="flex items-center gap-2">
                <span className={`font-medium ${!reimbursement.invoice_no ? 'text-red-500' : ''}`}>
                    {reimbursement.invoice_no ? `#${reimbursement.invoice_no}` : 'No Invoice #'}
                </span>
                {canEdit && (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Edit invoice number"
                    >
                        <Edit className="w-3 h-3" />
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="flex items-center gap-2">
            <input
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="border rounded px-2 py-1 text-sm w-32"
                placeholder="Invoice #"
                autoFocus
            />
            <button
                onClick={handleSave}
                disabled={saving || !value.trim()}
                className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                title="Save"
            >
                {saving ? (
                    <RefreshCw className="w-3 h-3 animate-spin" />
                ) : (
                    <Check className="w-3 h-3" />
                )}
            </button>
            <button
                onClick={handleCancel}
                className="text-red-600 hover:text-red-800 p-1"
                title="Cancel"
            >
                <X className="w-3 h-3" />
            </button>
        </div>
    );
};

const ReimbursementRow = ({
    reimbursement, onToggleSelect, isSelected, onEdit, formatCurrency, onRefresh, getAuthHeaders
}) => {
    const [expanded, setExpanded] = useState(false);
    const [transactionType, setTransactionType] = useState(null);

    // Add these permission check functions to your EnhancedReimbursementManager component
    const canEdit = useCallback((reimbursement) => {
        const status = reimbursement.approver_status ||
            reimbursement.approvalinfo?.status ||
            reimbursement.approval_status;
        return status === 'approved';
    }, []);

    const canSync = useCallback((reimbursement) => {
        const status = reimbursement.approver_status ||
            reimbursement.approvalinfo?.status ||
            reimbursement.approval_status;
        return status === 'approved';
    }, []);

    // Get approval status display
    const getApprovalStatusDisplay = useCallback((reimbursement) => {
        // Check multiple possible fields for approval status
        const status = reimbursement.approver_status ||
            reimbursement.approvalinfo?.status ||
            reimbursement.approval_status;

        console.log('Approval Status Debug:', {
            id: reimbursement.id,
            approver_status: reimbursement.approver_status,
            approvalinfo: reimbursement.approvalinfo,
            approval_status: reimbursement.approval_status,
            detected_status: status
        });

        // If no status found or status is null/undefined, treat as pending (not approved)
        if (!status) {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Pending Approval
                </span>
            );
        }

        if (status === 'approved') {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Approved
                    {reimbursement.approved_by && (
                        <span className="ml-1">by {reimbursement.approved_by}</span>
                    )}
                </span>
            );
        }

        if (status === 'pending_approval') {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Pending Approval
                </span>
            );
        }

        if (status === 'rejected') {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <XCircle className="w-3 h-3 mr-1" />
                    Rejected
                    {reimbursement.rejection_reason && (
                        <div className="text-xs mt-1" title={reimbursement.rejection_reason}>
                            Reason: {reimbursement.rejection_reason.substring(0, 30)}...
                        </div>
                    )}
                </span>
            );
        }

        return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {status}
            </span>
        );
    }, []);

    useEffect(() => {
        const determineType = () => {
            try {
                let hasGST = false;

                if (reimbursement.line_items_data) {
                    const lineItems = typeof reimbursement.line_items_data === 'string'
                        ? JSON.parse(reimbursement.line_items_data)
                        : reimbursement.line_items_data;

                    if (Array.isArray(lineItems)) {
                        hasGST = lineItems.some(item =>
                            (parseFloat(item.sgst_rate || 0) > 0) ||
                            (parseFloat(item.cgst_rate || 0) > 0) ||
                            (parseFloat(item.igst_rate || 0) > 0)
                        );
                    }
                }

                if (!hasGST) {
                    hasGST = (parseFloat(reimbursement.sgst_rate || 0) > 0) ||
                        (parseFloat(reimbursement.cgst_rate || 0) > 0) ||
                        (parseFloat(reimbursement.igst_rate || 0) > 0);
                }

                setTransactionType({
                    type: hasGST ? 'EXPENSE' : 'BILL',
                    hasGST: hasGST,
                    vendorType: hasGST ? 'ACTUAL_VENDOR' : 'EMPLOYEE_AS_VENDOR',
                    reason: hasGST ?
                        'Has GST - Create Expense with actual vendor' :
                        'No GST - Create Bill with employee as vendor (tax inclusive)'
                });
            } catch (error) {
                setTransactionType({
                    type: 'BILL',
                    hasGST: false,
                    vendorType: 'EMPLOYEE_AS_VENDOR',
                    reason: 'Error determining type - default to Bill with employee as vendor'
                });
            }
        };

        determineType();
    }, [reimbursement]);

    const isSynced = reimbursement.zoho_bill_id || reimbursement.zoho_expense_id || reimbursement.zoho_sync_status === 'synced';
    const isFailed = reimbursement.zoho_sync_status === 'failed';
    const isPending = !isSynced && !isFailed;
    const canSelect = !isSynced && canSync(reimbursement);

    const getStatusIcon = () => {
        if (isSynced) return <CheckCircle className="w-5 h-5 text-green-500" />;
        if (isFailed) return <XCircle className="w-5 h-5 text-red-500" />;
        return <Clock className="w-5 h-5 text-blue-500" />;
    };

    const getStatusText = () => {
        if (isSynced) {
            if (reimbursement.zoho_expense_id) return 'Synced as Expense';
            if (reimbursement.zoho_bill_id) return 'Synced as Purchase Bill';
            return 'Synced';
        }
        if (isFailed) return 'Failed - Can Retry';
        return 'Pending';
    };

    return (
        <>
            <tr className={`border-b hover:bg-gray-50 ${isFailed ? 'bg-red-50' : ''}`}>
                <td className="p-4">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleSelect(reimbursement.id)}
                        disabled={!canSelect}
                        className="rounded disabled:opacity-50"
                    />
                </td>

                <td className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                        {getStatusIcon()}
                        <span className="text-xs font-medium">{getStatusText()}</span>
                    </div>
                    {transactionType && (
                        <TransactionTypeIndicator
                            type={transactionType.type}
                            hasGST={transactionType.hasGST}
                            vendorType={transactionType.vendorType}
                            reason={transactionType.reason}
                        />
                    )}
                </td>

                <td className="p-4">
                    <InvoiceNumberEditor
                        reimbursement={reimbursement}
                        onUpdate={(updatedData) => {
                            onRefresh();
                        }}
                        getAuthHeaders={getAuthHeaders}
                    />
                    <div className="text-sm text-gray-600 mt-1">
                        Employee: {reimbursement.employee_name || 'N/A'}
                    </div>
                    {transactionType?.vendorType === 'ACTUAL_VENDOR' && (
                        <div className="text-sm text-gray-600">
                            Vendor: {reimbursement.vendor_name || 'Required for GST'}
                        </div>
                    )}
                    <div className="text-xs text-gray-500">
                        {reimbursement.date ? new Date(reimbursement.date).toLocaleDateString() : 'No date'}
                    </div>
                </td>

                <td className="p-4">
                    <div className="text-sm text-gray-900 mb-1">
                        {reimbursement.items?.substring(0, 50)}
                        {reimbursement.items?.length > 50 ? '...' : ''}
                    </div>

                    {reimbursement.line_items_data && (
                        <div className="text-xs text-blue-600">
                            {(() => {
                                try {
                                    const items = JSON.parse(reimbursement.line_items_data);
                                    return `${items.length} line item${items.length > 1 ? 's' : ''}`;
                                } catch {
                                    return 'Line items available';
                                }
                            })()}
                        </div>
                    )}

                    {reimbursement.aws_link && (
                        <a
                            href={reimbursement.aws_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-1"
                        >
                            <File className="w-3 h-3" />
                            View Document
                        </a>
                    )}
                </td>

                <td className="p-4">
                    {transactionType?.vendorType === 'EMPLOYEE_AS_VENDOR' ? (
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-blue-500" />
                                <span className="text-sm font-medium text-blue-700">
                                    Employee: {reimbursement.employee_name}
                                </span>
                            </div>
                            <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                Employee becomes vendor → Purchase Bill (tax inclusive)
                            </div>
                            <div className="text-xs text-gray-500">
                                Payment: Due on Receipt (0 days)
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <Building className="w-4 h-4 text-green-500" />
                                <span className="text-sm font-medium text-green-700">
                                    {reimbursement.vendor_name || 'Vendor Required'}
                                </span>
                            </div>
                            {reimbursement.gstin && (
                                <div className="text-xs text-green-600">
                                    GSTIN: {reimbursement.gstin}
                                </div>
                            )}
                            <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                                Actual vendor → Expense with proper GST handling
                            </div>
                            <div className="text-xs text-gray-500">
                                Payment: Employee Reimbursement Account
                            </div>
                        </div>
                    )}
                </td>

                <td className="p-4">
                    <div className="font-medium">{formatCurrency(reimbursement.total)}</div>
                    <div className="text-xs text-gray-500">
                        Currency: {reimbursement.currency || 'INR'}
                    </div>
                    {(reimbursement.sgst_rate > 0 || reimbursement.cgst_rate > 0 || reimbursement.igst_rate > 0) ? (
                        <div className="text-xs text-green-600">
                            GST: {reimbursement.igst_rate > 0 ?
                                `IGST ${reimbursement.igst_rate}%` :
                                `SGST ${reimbursement.sgst_rate}% + CGST ${reimbursement.cgst_rate}%`
                            }
                        </div>
                    ) : (
                        <div className="text-xs text-blue-600">
                            Tax Inclusive (No GST)
                        </div>
                    )}
                </td>

                <td className="p-4">
                    {getApprovalStatusDisplay(reimbursement)}
                </td>

                <td className="p-4">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setExpanded(!expanded)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                        >
                            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        <button
                            onClick={() => onEdit(reimbursement)}
                            disabled={isSynced || !canEdit(reimbursement)} // ADD canEdit check here
                            className={`p-1 ${isSynced || !canEdit(reimbursement)
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-blue-600 hover:text-blue-800'
                                }`}
                            title={
                                isSynced
                                    ? 'Cannot edit synced reimbursement'
                                    : !canEdit(reimbursement)
                                        ? `Cannot edit: ${reimbursement.approver_status === 'pending_approval' ? 'pending approval' : reimbursement.approver_status === 'rejected' ? 'rejected' : 'not approved'}`
                                        : 'Edit reimbursement'
                            }
                        >
                            <Edit className="w-4 h-4" />
                        </button>
                        {reimbursement.zoho_bill_id && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                Bill: {reimbursement.zoho_bill_id}
                            </span>
                        )}
                        {reimbursement.zoho_expense_id && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                                Exp: {reimbursement.zoho_expense_id}
                            </span>
                        )}
                        {isFailed && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                                Retry Available
                            </span>
                        )}
                    </div>
                </td>
            </tr>

            {expanded && (
                <tr className="bg-gray-50">
                    <td colSpan="7" className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h4 className="font-medium text-gray-900 mb-3">Transaction Details</h4>
                                <div className="bg-white p-3 rounded border space-y-2">
                                    <div className="flex items-center gap-2">
                                        {transactionType?.type === 'BILL' ? (
                                            <FileText className="w-4 h-4 text-blue-500" />
                                        ) : (
                                            <Wallet className="w-4 h-4 text-green-500" />
                                        )}
                                        <span className="font-medium">
                                            {transactionType?.type === 'BILL' ? 'Purchase Bill' : 'Expense'}
                                        </span>
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        <strong>Logic:</strong> {transactionType?.reason}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        <strong>Vendor:</strong> {transactionType?.vendorType === 'EMPLOYEE_AS_VENDOR' ?
                                            `${reimbursement.employee_name} (Employee as vendor)` :
                                            `${reimbursement.vendor_name || 'Required'} (Actual vendor)`
                                        }
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        <strong>Tax Treatment:</strong> {transactionType?.hasGST ?
                                            'Proper GST handling with tax rates' :
                                            'Tax Inclusive (No separate GST)'
                                        }
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        <strong>Payment:</strong> {transactionType?.type === 'BILL' ?
                                            'Due on Receipt (0 days)' :
                                            'Employee Reimbursement Account'
                                        }
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h4 className="font-medium text-gray-900 mb-3">Sync Status</h4>
                                <div className="bg-white p-3 rounded border space-y-2">
                                    <div className="flex items-center gap-2">
                                        {getStatusIcon()}
                                        <span className="font-medium">{getStatusText()}</span>
                                    </div>
                                    {reimbursement.zoho_sync_date && (
                                        <div className="text-sm text-gray-600">
                                            <strong>Sync Date:</strong> {new Date(reimbursement.zoho_sync_date).toLocaleString()}
                                        </div>
                                    )}
                                    {reimbursement.zoho_sync_error && (
                                        <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                                            <strong>Error:</strong> {reimbursement.zoho_sync_error}
                                            <div className="text-xs mt-1">Can be retried by selecting and syncing again.</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

// Enhanced Sync Results Modal Component
const SyncResultsModal = ({ isOpen, onClose, results }) => {
    if (!isOpen || !results) return null;

    const { summary, results: syncResults } = results;
    const hasErrors = syncResults.some(r => !r.success);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-60">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-90vh overflow-y-auto">
                {/* Header */}
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900">
                            Sync Results
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Summary */}
                <div className="p-6 border-b border-gray-200">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-gray-900">{summary.total}</div>
                            <div className="text-sm text-gray-500">Total</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">{summary.successful}</div>
                            <div className="text-sm text-gray-500">Successful</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-red-600">{summary.failed}</div>
                            <div className="text-sm text-gray-500">Failed</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">{summary.expenses_created || 0}</div>
                            <div className="text-sm text-gray-500">Expenses</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-purple-600">{summary.bills_created || 0}</div>
                            <div className="text-sm text-gray-500">Bills</div>
                        </div>
                    </div>
                </div>

                {/* Results Details */}
                <div className="p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Detailed Results</h3>
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                        {syncResults.map((result) => (
                            <div
                                key={result.reimbursement_id}
                                className={`border rounded-lg p-4 ${result.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                                    }`}
                            >
                                <div className="flex items-start gap-3">
                                    {result.success ? (
                                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                                    ) : (
                                        <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                                    )}

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="font-medium text-gray-900">
                                                ID: {result.reimbursement_id}
                                            </span>
                                            {result.transaction_type && (
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${result.transaction_type === 'EXPENSE'
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-blue-100 text-blue-800'
                                                    }`}>
                                                    {result.transaction_type}
                                                </span>
                                            )}
                                        </div>

                                        <div className="text-sm mb-2">
                                            {result.success ? (
                                                <span className="text-green-700">
                                                    ✅ {result.message}
                                                    {result.zoho_id && (
                                                        <span className="ml-2 font-mono text-xs">
                                                            ({result.zoho_id})
                                                        </span>
                                                    )}
                                                </span>
                                            ) : (
                                                <div className="text-red-700">
                                                    ❌ {result.message}
                                                    {result.error && result.error !== result.message && (
                                                        <div className="text-sm mt-1 bg-red-100 p-2 rounded">
                                                            <strong>Details:</strong> {result.error}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {result.vendor_info && (
                                            <div className="text-xs text-gray-600">
                                                Vendor: {result.vendor_info.contact_name} ({result.vendor_info.contact_id})
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-200">
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Main Enhanced Reimbursement Manager Component
const EnhancedReimbursementManager = () => {
    const [reimbursements, setReimbursements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedReimbursements, setSelectedReimbursements] = useState([]);
    const [syncInProgress, setSyncInProgress] = useState(false);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingReimbursement, setEditingReimbursement] = useState(null);

    const [syncResults, setSyncResults] = useState(null);
    const [syncResultModalOpen, setSyncResultModalOpen] = useState(false);

    // Internal organization and auth state
    const [currentOrganization, setCurrentOrganization] = useState({
        organization_id: '',
        name: '',
        gstin: ''
    });
    const [tokenStatus, setTokenStatus] = useState('checking');

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
    });

    // Get authentication headers
    const getAuthHeaders = () => {
        const token = localStorage.getItem('authToken') || localStorage.getItem('auth_token') || localStorage.getItem('token');
        return {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
        };
    };

    // Fetch current organization
    const fetchCurrentOrganization = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/token/status`, {
                headers: getAuthHeaders()
            });
            const result = await response.json();

            console.log('Organization switched:', result.data?.current_organization);
            console.log('Token status:', result.data?.token_status);

            if (result.success && result.data.current_organization) {
                const org = result.data.current_organization;
                setCurrentOrganization(org);
                setTokenStatus(result.data.token_status || 'valid');
                return org.organization_id;
            } else {
                setTokenStatus('invalid');
                return null;
            }
        } catch (error) {
            console.error('Error fetching current organization:', error);
            setTokenStatus('invalid');
            return null;
        }
    }, []);

    const fetchReimbursements = async () => {
        if (!currentOrganization?.organization_id) return;

        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: pagination.page,
                limit: pagination.limit,
                company_id: currentOrganization.organization_id
            });

            if (searchTerm) params.append('search', searchTerm);
            if (filter !== 'all') params.append('status', filter);

            const response = await fetch(`${API_BASE_URL}/reimbursements/with-expense-data?${params}`, {
                headers: getAuthHeaders()
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setReimbursements(data.data.reimbursements);
                    setPagination(prev => ({
                        ...prev,
                        total: data.data.pagination.total,
                        totalPages: data.data.pagination.totalPages
                    }));
                }
            }
        } catch (error) {
            console.error('Error fetching reimbursements:', error);
        } finally {
            setLoading(false);
        }
    };

    const canEdit = useCallback((reimbursement) => {
        const status = reimbursement.approver_status || reimbursement.approvalinfo?.status;

        // Can only edit if approved or no status (defaults to approved)
        return status === 'approved' || !status;
    }, []);

    const canSync = useCallback((reimbursement) => {
        const status = reimbursement.approver_status || reimbursement.approvalinfo?.status;

        // Can only sync if approved or no status (defaults to approved)
        return status === 'approved' || !status;
    }, []);

    const getApprovalStatusDisplay = useCallback((reimbursement) => {
        const status = reimbursement.approver_status || reimbursement.approvalinfo?.status;

        if (!status || status === 'approved') {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Approved
                    {reimbursement.approver_name && (
                        <span className="ml-1">by {reimbursement.approver_name}</span>
                    )}
                </span>
            );
        }

        if (status === 'pending_approval') {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Pending Approval
                </span>
            );
        }

        if (status === 'rejected') {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <XCircle className="w-3 h-3 mr-1" />
                    Rejected
                    {reimbursement.rejection_reason && (
                        <div className="text-xs mt-1" title={reimbursement.rejection_reason}>
                            Reason: {reimbursement.rejection_reason.substring(0, 30)}...
                        </div>
                    )}
                </span>
            );
        }

        return null;
    }, []);

    const syncSelectedReimbursements = async () => {
        if (selectedReimbursements.length === 0) {
            alert('No reimbursements selected for sync.');
            return;
        }

        // Filter for valid reimbursements that can be synced (approved only)
        const validReimbursements = reimbursements.filter(r => {
            const isSelected = selectedReimbursements.includes(r.id);
            const canSyncReimbursement = canSync(r);
            const isNotSynced = !r.zoho_bill_id && !r.zoho_expense_id && r.zoho_sync_status !== 'synced';

            return isSelected && canSyncReimbursement && isNotSynced;
        });

        // Check if any selected reimbursements are not approved
        const unapprovedSelected = reimbursements.filter(r =>
            selectedReimbursements.includes(r.id) && !canSync(r)
        );

        if (unapprovedSelected.length > 0) {
            const statusCounts = unapprovedSelected.reduce((acc, reimbursement) => {
                const status = reimbursement.approver_status || reimbursement.approvalinfo?.status || 'unknown';
                acc[status] = (acc[status] || 0) + 1;
                return acc;
            }, {});

            let message = 'Cannot sync the following reimbursements:\n';
            Object.entries(statusCounts).forEach(([status, count]) => {
                const statusText = status === 'pending_approval' ? 'Pending Approval' :
                    status === 'rejected' ? 'Rejected' : 'Unknown Status';
                message += `• ${count} reimbursements with ${statusText}\n`;
            });
            message += '\nOnly approved reimbursements can be synced.';

            alert(message);
            return;
        }

        if (validReimbursements.length === 0) {
            alert('No valid approved reimbursements selected for sync.');
            return;
        }

        setSyncInProgress(true);

        try {
            const reimbursementUpdates = selectedReimbursements.map(id => ({
                reimbursement_id: id
            }));

            console.log('Sending sync request with auth headers:', getAuthHeaders());
            console.log('Request payload:', { reimbursementUpdates });

            const response = await fetch(`${API_BASE_URL}/reimbursements/sync-enhanced`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ reimbursementUpdates })
            });

            console.log('Response status:', response.status);

            if (response.ok) {
                const result = await response.json();
                console.log('✅ Sync result:', result);

                // Show results in modal instead of alert
                setSyncResults(result);
                setSyncResultModalOpen(true);

                setSelectedReimbursements([]);
                await fetchReimbursements();
            }
            else {
                // This is for actual HTTP errors (401, 500, etc)
                const errorData = await response.json().catch(async () => {
                    return { message: await response.text() };
                });

                // If there's detailed error information, show the modal
                if (errorData.results && errorData.results.length > 0) {
                    setSyncResults(errorData);
                    setSyncResultModalOpen(true);
                } else {
                    alert(`Sync request failed: ${response.status} - ${JSON.stringify(errorData)}`);
                }
            }
        } finally {
            setSyncInProgress(false);
        }
    };

    const handleEdit = async (reimbursement) => {
        // Check approval status before attempting to edit
        const canEditReimbursement = canEdit(reimbursement);

        if (!canEditReimbursement) {
            const status = reimbursement.approver_status ||
                reimbursement.approvalInfo?.status ||
                reimbursement.approval_status;

            let message = 'Cannot edit this reimbursement: ';
            if (status === 'pending_approval') {
                message += 'it is pending approval.';
            } else if (status === 'rejected') {
                message += 'it has been rejected.';
            } else {
                message += 'it is not approved for editing.';
            }

            alert(message);
            return;
        }
        try {
            const response = await fetch(`${API_BASE_URL}/reimbursements/${reimbursement.id}/edit-details`, {
                headers: getAuthHeaders()
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    setEditingReimbursement(result.data);
                    setEditModalOpen(true);
                }
            }
        } catch (error) {
            console.error('Error fetching reimbursement for edit:', error);
            alert('Failed to load reimbursement details: ' + error.message);
        }
    };

    const handleSaveEdit = (updatedReimbursement) => {
        setReimbursements(prev =>
            prev.map(r => r.id === updatedReimbursement.id ? updatedReimbursement : r)
        );
        fetchReimbursements();
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount || 0);
    };

    // Initialize component
    useEffect(() => {
        fetchCurrentOrganization();
    }, [fetchCurrentOrganization]);

    useEffect(() => {
        if (currentOrganization?.organization_id && tokenStatus === 'valid') {
            fetchReimbursements();
        }
    }, [pagination.page, searchTerm, filter, currentOrganization?.organization_id, tokenStatus]);

    const filteredReimbursements = reimbursements.filter(reimbursement => {
        const isSynced = reimbursement.zoho_bill_id || reimbursement.zoho_expense_id || reimbursement.zoho_sync_status === 'synced';
        const isFailed = reimbursement.zoho_sync_status === 'failed';
        const isPending = !isSynced && !isFailed;

        const matchesSearch = !searchTerm ||
            reimbursement.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            reimbursement.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            reimbursement.invoice_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            reimbursement.items?.toLowerCase().includes(searchTerm.toLowerCase());

        if (!matchesSearch) return false;

        switch (filter) {
            case 'synced': return isSynced;
            case 'failed': return isFailed;
            case 'pending': return isPending;
            default: return true;
        }
    });

    const stats = {
        total: reimbursements.length,
        synced: reimbursements.filter(r => r.zoho_bill_id || r.zoho_expense_id || r.zoho_sync_status === 'synced').length,
        failed: reimbursements.filter(r => r.zoho_sync_status === 'failed').length,
        pending: reimbursements.filter(r => !r.zoho_bill_id && !r.zoho_expense_id && r.zoho_sync_status !== 'synced' && r.zoho_sync_status !== 'failed').length,
        expenses: reimbursements.filter(r => r.zoho_expense_id).length,
        bills: reimbursements.filter(r => r.zoho_bill_id).length
    };

    if (!currentOrganization?.organization_id) {
        return (
            <div className="p-6 text-center">
                <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Organization Selected</h3>
                <p className="text-gray-600">Please select an organization to view reimbursements.</p>
            </div>
        );
    }

    if (tokenStatus !== 'valid') {
        return (
            <div className="p-6 text-center">
                <Shield className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Authentication Required</h3>
                <p className="text-gray-600">Please authenticate with Zoho to sync reimbursements.</p>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto p-6 space-y-6">
            {/* Edit Modal */}
            <EditReimbursementModal
                reimbursement={editingReimbursement}
                isOpen={editModalOpen}
                onClose={() => {
                    setEditModalOpen(false);
                    setEditingReimbursement(null);
                }}
                onSave={handleSaveEdit}
                getAuthHeaders={getAuthHeaders}
                canEdit={canEdit}
                canSync={canSync}
            />

            {/* Header */}
            <div className="bg-white rounded-lg border p-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                            <Zap className="w-8 h-8 text-blue-600" />
                            Enhanced Reimbursement Manager
                            <div className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                                <Sparkles className="w-4 h-4" />
                                AI Powered
                            </div>
                        </h1>
                        <p className="text-gray-600 mt-2">
                            <strong>YOUR LOGIC:</strong> No GST → Employee as Vendor → Purchase Bill | Has GST → Actual Vendor → Expense
                        </p>
                        <div className="flex items-center gap-4 mt-2">
                            <p className="text-gray-600">
                                Organization: <span className="font-medium text-blue-600">{currentOrganization.name}</span>
                            </p>
                            <p className="text-gray-600">
                                GSTIN: <span className="font-medium text-green-600">{currentOrganization.gstin || 'Not Set'}</span>
                            </p>
                            {currentOrganization.gstin && (
                                <p className="text-gray-600">
                                    Place of Supply: <span className="font-medium text-purple-600">
                                        {GST_STATE_MAPPING[currentOrganization.gstin.substring(0, 2)] || 'Unknown'}
                                    </span>
                                </p>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={() => fetchReimbursements()}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh Data
                    </button>
                </div>

                {/* Enhanced Stats Dashboard */}
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <FileText className="w-8 h-8 text-gray-600" />
                            <div>
                                <h3 className="text-2xl font-bold text-gray-900">{stats.total}</h3>
                                <p className="text-sm text-gray-600">Total</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <Clock className="w-8 h-8 text-blue-600" />
                            <div>
                                <h3 className="text-2xl font-bold text-blue-900">{stats.pending}</h3>
                                <p className="text-sm text-blue-600">Pending</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <CheckCircle className="w-8 h-8 text-green-600" />
                            <div>
                                <h3 className="text-2xl font-bold text-green-900">{stats.synced}</h3>
                                <p className="text-sm text-green-600">Synced</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <XCircle className="w-8 h-8 text-red-600" />
                            <div>
                                <h3 className="text-2xl font-bold text-red-900">{stats.failed}</h3>
                                <p className="text-sm text-red-600">Failed - Retryable</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <Wallet className="w-8 h-8 text-emerald-600" />
                            <div>
                                <h3 className="text-2xl font-bold text-emerald-900">{stats.expenses}</h3>
                                <p className="text-sm text-emerald-600">Expenses (GST)</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <Receipt className="w-8 h-8 text-indigo-600" />
                            <div>
                                <h3 className="text-2xl font-bold text-indigo-900">{stats.bills}</h3>
                                <p className="text-sm text-indigo-600">Bills (No GST)</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search and Filter */}
            <div className="bg-white rounded-lg border p-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1">
                        <div className="flex-1 relative max-w-md">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                            <input
                                type="text"
                                placeholder="Search reimbursements..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <Filter className="w-5 h-5 text-gray-500" />
                            <div className="flex gap-2">
                                {['all', 'pending', 'synced', 'failed'].map(filterType => (
                                    <button
                                        key={filterType}
                                        onClick={() => setFilter(filterType)}
                                        className={`px-3 py-1 rounded-full text-sm font-medium ${filter === filterType
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        {selectedReimbursements.length > 0 && (
                            <span className="text-sm text-gray-600">
                                {selectedReimbursements.length} selected
                            </span>
                        )}

                        <button
                            onClick={() => {
                                const selectableIds = filteredReimbursements
                                    .filter(r => r.zoho_sync_status !== 'synced')
                                    .map(r => r.id);
                                setSelectedReimbursements(selectableIds);
                            }}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                            Select All Available ({stats.pending + stats.failed})
                        </button>

                        <button
                            onClick={() => setSelectedReimbursements([])}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                            Clear Selection
                        </button>

                        {selectedReimbursements.length > 0 && (
                            <button
                                onClick={syncSelectedReimbursements}
                                disabled={syncInProgress}
                                className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                            >
                                {syncInProgress ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        Syncing...
                                    </>
                                ) : (
                                    <>
                                        <Zap className="w-4 h-4" />
                                        Smart Sync {selectedReimbursements.length}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Process Flow Info */}
            <div className="bg-white rounded-lg border p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-500" />
                    Your Smart Processing Logic with AI
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <FileText className="w-5 h-5 text-blue-600" />
                            <h4 className="font-semibold text-blue-900">NO GST → Purchase Bill</h4>
                        </div>
                        <ul className="text-sm text-blue-700 space-y-1">
                            <li>• All line items have GST rates = 0</li>
                            <li>• Employee becomes the vendor in Zoho</li>
                            <li>• Creates Purchase Bill with employee as vendor</li>
                            <li>• Tax-inclusive amounts (uses total_with_tax)</li>
                            <li>• AI suggests expense accounts per line item</li>
                            <li>• Payment terms: Due on Receipt (0 days)</li>
                            <li>• Place of supply from organization GSTIN</li>
                        </ul>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <Wallet className="w-5 h-5 text-green-600" />
                            <h4 className="font-semibold text-green-900">HAS GST → Expense</h4>
                        </div>
                        <ul className="text-sm text-green-700 space-y-1">
                            <li>• Any line item has GST rate greater than 0</li>
                            <li>• Uses actual vendor from reimbursement data</li>
                            <li>• Creates Expense with proper vendor</li>
                            <li>• Proper GST tax handling (SGST+CGST or IGST)</li>
                            <li>• GSTIN-based vendor classification</li>
                            <li>• AI suggests Employee Reimbursement account</li>
                            <li>• ITC handling for tax credit management</li>
                        </ul>
                    </div>
                </div>
                <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-sm text-purple-800">
                        <strong>🤖 AI Features:</strong> OpenAI GPT-4o-mini powered expense account suggestions.
                        Auto payment account detection. Smart ITC classification. Failed status retry capability.
                        Invoice number editing for non-synced items.
                    </p>
                </div>
            </div>

            {/* Reimbursement Table */}
            <div className="bg-white rounded-lg border">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">Smart Reimbursement Processing</h2>
                    <p className="text-gray-600 mt-1">No GST → Bills | Has GST → Expenses with AI-powered vendor and account handling</p>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="text-left p-4">
                                    <input
                                        type="checkbox"
                                        checked={selectedReimbursements.length === filteredReimbursements.filter(r =>
                                            r.zoho_sync_status !== 'synced'
                                        ).length &&
                                            filteredReimbursements.filter(r =>
                                                r.zoho_sync_status !== 'synced'
                                            ).length > 0}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                const selectableIds = filteredReimbursements
                                                    .filter(r => r.zoho_sync_status !== 'synced')
                                                    .map(r => r.id);
                                                setSelectedReimbursements(selectableIds);
                                            } else {
                                                setSelectedReimbursements([]);
                                            }
                                        }}
                                        className="rounded"
                                    />
                                </th>
                                <th className="text-left p-4">Status & Type</th>
                                <th className="text-left p-4">Invoice & Employee</th>
                                <th className="text-left p-4">Items & Documents</th>
                                <th className="text-left p-4">Vendor & Processing</th>
                                <th className="text-left p-4">Amount & Tax</th>
                                <th className="text-left p-4">Approval Status</th>
                                <th className="text-left p-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                                        Loading reimbursements...
                                    </td>
                                </tr>
                            ) : filteredReimbursements.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                        <h3 className="text-lg font-medium text-gray-900 mb-2">No reimbursements found</h3>
                                        <p className="text-gray-500">No reimbursements match the current filter criteria.</p>
                                    </td>
                                </tr>
                            ) : (
                                filteredReimbursements.map((reimbursement) => (
                                    <ReimbursementRow
                                        key={reimbursement.id}
                                        reimbursement={reimbursement}
                                        isSelected={selectedReimbursements.includes(reimbursement.id)}
                                        onToggleSelect={(id) => {
                                            setSelectedReimbursements(prev =>
                                                prev.includes(id)
                                                    ? prev.filter(rid => rid !== id)
                                                    : [...prev, id]
                                            );
                                        }}
                                        onEdit={handleEdit}
                                        formatCurrency={formatCurrency}
                                        onRefresh={fetchReimbursements}
                                        getAuthHeaders={getAuthHeaders}
                                    />
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <div className="bg-white rounded-lg border p-4">
                <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-700">
                        Showing {Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)} to{' '}
                        {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                            disabled={pagination.page === 1}
                            className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                            Previous
                        </button>
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded">
                            {pagination.page} / {pagination.totalPages || 1}
                        </span>
                        <button
                            onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.totalPages, prev.page + 1) }))}
                            disabled={pagination.page >= pagination.totalPages}
                            className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
            {/* ADD THIS SYNC RESULTS MODAL */}
            <SyncResultsModal
                isOpen={syncResultModalOpen}
                onClose={() => setSyncResultModalOpen(false)}
                results={syncResults}
            />
        </div>
    );
};

export default EnhancedReimbursementManager;